import { Header } from '@/components/layout/Header'
import { Footer } from '@/components/layout/Footer'
import { SEO, StructuredData } from '@/components/seo'

export default function CookiesPage() {
  return (
    <div className="min-h-screen bg-surface-950 text-white">
      <SEO
        title="Cookie Policy"
        description="JumpstoneHR Cookie Policy. Learn about the cookies we use, why we use them, and how to manage your cookie preferences."
        canonical="/cookies"
        structuredData={[
          StructuredData.webPage(
            'Cookie Policy',
            'JumpstoneHR cookie policy explaining cookie usage and management.',
            '/cookies'
          ),
          StructuredData.breadcrumb([
            { name: 'Home', url: '/' },
            { name: 'Cookie Policy', url: '/cookies' },
          ]),
        ]}
      />
      <Header />
      
      <main>
        {/* Hero Section */}
        <section className="relative pt-32 pb-16 overflow-hidden">
          <div className="absolute inset-0 opacity-[0.03]">
            <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
              <defs>
                <pattern id="grid" width="10" height="10" patternUnits="userSpaceOnUse">
                  <path d="M 10 0 L 0 0 0 10" fill="none" stroke="white" strokeWidth="0.5"/>
                </pattern>
              </defs>
              <rect width="100%" height="100%" fill="url(#grid)" />
            </svg>
          </div>
          
          <div className="relative mx-auto max-w-4xl section-padding">
            <div className="flex items-center gap-3 mb-8">
              <div className="h-px flex-1 max-w-[60px] bg-brand-500" />
              <span className="text-brand-400 text-sm font-medium tracking-widest uppercase">Legal</span>
            </div>
            
            <h1 className="text-4xl sm:text-5xl font-bold tracking-tight mb-6">
              Cookie Policy
            </h1>
            
            <p className="text-surface-400">
              Last updated: January 1, 2026
            </p>
          </div>
        </section>

        {/* Content */}
        <section className="pb-24">
          <div className="mx-auto max-w-4xl section-padding">
            <div className="prose prose-invert prose-lg max-w-none">
              <div className="space-y-12">
                <div>
                  <h2 className="text-2xl font-bold text-white mb-4">What Are Cookies?</h2>
                  <p className="text-surface-300 leading-relaxed">
                    Cookies are small text files that are placed on your computer or mobile device when you visit a website. They are widely used to make websites work more efficiently and to provide information to website owners. This Cookie Policy explains how Jumpstone Technology Inc. ("we", "us", or "our") uses cookies and similar technologies on JumpstoneHR.
                  </p>
                </div>

                <div>
                  <h2 className="text-2xl font-bold text-white mb-4">How We Use Cookies</h2>
                  <p className="text-surface-300 leading-relaxed mb-4">We use cookies for several purposes:</p>
                  
                  <div className="space-y-6">
                    <div className="bg-surface-900 border border-surface-800 rounded-xl p-6">
                      <h3 className="text-lg font-semibold text-white mb-2">Essential Cookies</h3>
                      <p className="text-surface-400">
                        These cookies are necessary for the website to function properly. They enable core functionality such as security, account access, and remembering your preferences. You cannot opt out of these cookies.
                      </p>
                    </div>

                    <div className="bg-surface-900 border border-surface-800 rounded-xl p-6">
                      <h3 className="text-lg font-semibold text-white mb-2">Authentication Cookies</h3>
                      <p className="text-surface-400">
                        These cookies help us identify you when you log in to your account and keep you logged in as you navigate between pages. They are essential for maintaining your session security.
                      </p>
                    </div>

                    <div className="bg-surface-900 border border-surface-800 rounded-xl p-6">
                      <h3 className="text-lg font-semibold text-white mb-2">Analytics Cookies</h3>
                      <p className="text-surface-400">
                        We use analytics cookies to understand how visitors interact with our website. This helps us improve our service and user experience. These cookies collect information in an aggregated, anonymous form.
                      </p>
                    </div>

                    <div className="bg-surface-900 border border-surface-800 rounded-xl p-6">
                      <h3 className="text-lg font-semibold text-white mb-2">Preference Cookies</h3>
                      <p className="text-surface-400">
                        These cookies remember your settings and preferences, such as language choice and display settings, to provide a more personalized experience.
                      </p>
                    </div>
                  </div>
                </div>

                <div>
                  <h2 className="text-2xl font-bold text-white mb-4">Specific Cookies We Use</h2>
                  <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                      <thead>
                        <tr className="border-b border-surface-700">
                          <th className="py-3 px-4 text-white font-semibold">Cookie Name</th>
                          <th className="py-3 px-4 text-white font-semibold">Purpose</th>
                          <th className="py-3 px-4 text-white font-semibold">Duration</th>
                        </tr>
                      </thead>
                      <tbody className="text-surface-300">
                        <tr className="border-b border-surface-800">
                          <td className="py-3 px-4 font-mono text-sm">session_id</td>
                          <td className="py-3 px-4">Maintains your login session</td>
                          <td className="py-3 px-4">Session</td>
                        </tr>
                        <tr className="border-b border-surface-800">
                          <td className="py-3 px-4 font-mono text-sm">csrf_token</td>
                          <td className="py-3 px-4">Security protection</td>
                          <td className="py-3 px-4">Session</td>
                        </tr>
                        <tr className="border-b border-surface-800">
                          <td className="py-3 px-4 font-mono text-sm">preferences</td>
                          <td className="py-3 px-4">Stores your display preferences</td>
                          <td className="py-3 px-4">1 year</td>
                        </tr>
                        <tr className="border-b border-surface-800">
                          <td className="py-3 px-4 font-mono text-sm">_analytics</td>
                          <td className="py-3 px-4">Anonymous usage analytics</td>
                          <td className="py-3 px-4">2 years</td>
                        </tr>
                        <tr className="border-b border-surface-800">
                          <td className="py-3 px-4 font-mono text-sm">cookie_consent</td>
                          <td className="py-3 px-4">Records your cookie preferences</td>
                          <td className="py-3 px-4">1 year</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                <div>
                  <h2 className="text-2xl font-bold text-white mb-4">Third-Party Cookies</h2>
                  <p className="text-surface-300 leading-relaxed mb-4">
                    We may use third-party services that set their own cookies. These include:
                  </p>
                  <ul className="list-disc list-inside text-surface-300 space-y-2 ml-4">
                    <li><strong className="text-white">Payment processors</strong> – to securely process payments</li>
                    <li><strong className="text-white">Analytics providers</strong> – to help us understand website usage</li>
                    <li><strong className="text-white">Security services</strong> – to protect against fraud and abuse</li>
                  </ul>
                  <p className="text-surface-300 leading-relaxed mt-4">
                    We do not control these third-party cookies. Please refer to the respective privacy policies of these services for more information about their cookie practices.
                  </p>
                </div>

                <div>
                  <h2 className="text-2xl font-bold text-white mb-4">Managing Cookies</h2>
                  <p className="text-surface-300 leading-relaxed mb-4">
                    Most web browsers allow you to control cookies through their settings. You can typically:
                  </p>
                  <ul className="list-disc list-inside text-surface-300 space-y-2 ml-4">
                    <li>View what cookies are stored on your device</li>
                    <li>Delete all or specific cookies</li>
                    <li>Block cookies from being set</li>
                    <li>Set preferences for certain websites</li>
                  </ul>
                  <p className="text-surface-300 leading-relaxed mt-4">
                    Please note that blocking essential cookies may prevent you from using some features of our Service. If you delete cookies, you may need to log in again and some preferences may be lost.
                  </p>
                </div>

                <div>
                  <h2 className="text-2xl font-bold text-white mb-4">Browser-Specific Instructions</h2>
                  <p className="text-surface-300 leading-relaxed mb-4">
                    To manage cookies in your browser, visit your browser's help documentation:
                  </p>
                  <ul className="list-disc list-inside text-surface-300 space-y-2 ml-4">
                    <li>Google Chrome: Settings → Privacy and security → Cookies</li>
                    <li>Mozilla Firefox: Settings → Privacy & Security → Cookies</li>
                    <li>Safari: Preferences → Privacy → Cookies</li>
                    <li>Microsoft Edge: Settings → Cookies and site permissions</li>
                  </ul>
                </div>

                <div>
                  <h2 className="text-2xl font-bold text-white mb-4">Do Not Track</h2>
                  <p className="text-surface-300 leading-relaxed">
                    Some browsers offer a "Do Not Track" (DNT) feature that sends a signal to websites you visit indicating you do not wish to be tracked. Currently, there is no universally accepted standard for how to respond to DNT signals. We currently do not respond to DNT signals but may update this policy as standards evolve.
                  </p>
                </div>

                <div>
                  <h2 className="text-2xl font-bold text-white mb-4">Changes to This Policy</h2>
                  <p className="text-surface-300 leading-relaxed">
                    We may update this Cookie Policy from time to time to reflect changes in our practices or for legal, operational, or regulatory reasons. We will post the updated policy on this page with a new "Last updated" date.
                  </p>
                </div>

                <div>
                  <h2 className="text-2xl font-bold text-white mb-4">Contact Us</h2>
                  <p className="text-surface-300 leading-relaxed mb-4">
                    If you have questions about our use of cookies, please contact us:
                  </p>
                  <div className="bg-surface-900 border border-surface-800 rounded-xl p-6">
                    <p className="text-white font-semibold">Jumpstone Technology Inc.</p>
                    <p className="text-surface-400 mt-2">Email: privacy@jumpstonehr.com</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      
      <Footer />
    </div>
  )
}

